import {
  useInfiniteQuery,
  useOwnerPortalContact,
  useTeam,
} from '@vrplatform/data';
import type { MRT_SortingState } from '@vrplatform/table';
import { sum } from '@vrplatform/utils';
import type { reservation as Reservation } from '@vrplatform/graphql';
import {
  getListingName,
  queryReservationFinancials,
  queryReservationPaymentLines,
} from '@vrplatform/ui-common';
import { useWhereReservations } from './useWhereReservations';

type Params = {
  sorting: MRT_SortingState;
  filterByAutomationId: string | undefined;
  hideCancellations: boolean;
};

export type ReservationRow = NonNullable<
  ReturnType<typeof useReservationTableQuery>['data']
>['pages'][number]['list'][number];

export const getReservationTableRow = (
  reservation: Reservation,
  opts: {
    partnerId: string;
    tenantId: string;
    GL: boolean;
  }
) => {
  const { partnerId, tenantId, GL } = opts;

  const legacyFinancials = GL
    ? []
    : queryReservationFinancials(reservation, {
        partnerId,
        tenantId,
        GL,
      });

  const ledgerFinancials = {
    total: reservation.centTotal ?? 0,
    paid: reservation.centPaid ?? 0,
    unpaid: (reservation.centTotal ?? 0) - (reservation.centPaid ?? 0),
  };

  const included = legacyFinancials.filter((x) => x.isInvoice === 'invoice');

  const paymentLines = queryReservationPaymentLines(reservation, {
    includeAllType2s: true,
  }).filter((x) => x.isResevationPayment);

  const total = GL ? ledgerFinancials.total : sum(included, 'centTotal');
  const paid = GL ? ledgerFinancials.paid : sum(paymentLines, 'centTotal');
  const unpaid = GL ? ledgerFinancials.unpaid : total - paid;

  const listing = GL
    ? {
        id: reservation?.listing?.id,
        name: getListingName(reservation?.listing),
      }
    : {
        id: reservation?.listingConnection?.listing?.id,
        name: getListingName(reservation?.listingConnection?.listing),
      };

  const isPriorToStartDate = !GL
    ? false
    : !reservation
        .journalEntries({
          where: {
            status: { _eq: 'active' },
          },
          limit: 1,
        })
        .map((x) => x.id)[0];

  const hasUnmappedLine = !GL
    ? false
    : !!reservation
        .paymentLines({
          where: {
            paymentId: { _is_null: true },
            type2: { _is_null: false },
            _not: {
              lineTypeAccounts: {},
            },
          },
          limit: 1,
        })
        .map((x) => x.id)[0];

  return {
    id: reservation.id,
    isPriorToStartDate,
    connection: {
      id: reservation.connection?.id,
      name: reservation.connection?.name,
      app: {
        id: reservation?.connection?.app?.id,
        iconRound: reservation?.connection?.app?.iconRound,
      },
    },
    hasUnmappedLine,
    bookedAt: reservation.bookedAt,
    cancelledAt: reservation.cancelledAt,
    checkIn: reservation.checkIn,
    checkOut: reservation.checkOut,
    nights: reservation.nights,
    bookingPlatform: reservation.bookingPlatform,
    confirmationCode: reservation.confirmationCode,
    guestName: reservation.guestName,
    status: reservation.status,
    paidStatus: reservation.paidStatus,
    currency: reservation.currency,
    listing,
    tenant: {
      id: reservation.tenant?.id,
      name: reservation.tenant?.name,
      logo: reservation.tenant?.logo,
      colorPrimary: reservation.tenant?.colorPrimary,
    },
    paid,
    total,
    unpaid,
  };
};

export const useReservationTableQuery = ({
  sorting,
  filterByAutomationId,
  hideCancellations,
}: Params) => {
  const { contactId: ownerPortalContactId } = useOwnerPortalContact();

  const where = useWhereReservations({
    filterByAutomationId,
    hideCancellations,
  });
  const [{ isGL: GL, id: tenantId, partnerId }] = useTeam();

  return useInfiniteQuery(
    (q, { where, tenantId, partnerId, GL }, { limit, offset }) => {
      const aggregate =
        q.reservationAggregate({ where }).aggregate?.count() || 0;

      const order_by = sorting.map((sort) => ({
        [sort.id]: sort.desc ? 'desc_nulls_last' : 'asc_nulls_last',
      }));

      const list = q
        .reservations({
          where,
          limit,
          offset,
          order_by,
        })
        .map((reservation) =>
          getReservationTableRow(reservation, {
            partnerId,
            tenantId,
            GL,
          })
        );

      return {
        list,
        aggregate,
      };
    },
    {
      queryKey: 'reservations',
      variables: {
        where,
        tenantId,
        partnerId,
        sorting,
        ownerPortalContactId,
        GL,
      },
    }
  );
};
